import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    inject
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import {
    LgTranslateService,
    LG_LOCALIZATION_SETTINGS,
    useTranslationNamespace
} from "@logex/framework/lg-localization";
import { IDropdownDefinition } from "@logex/framework/ui-core";
import { BaseWithDefinitionsComponent } from "@mrdm-self-service-frontend/shared/bases";
import {
    DefinitionJobTitleCategory,
    DefinitionKey,
    EXCLUDED_JOB_TITLE_CATEGORY_CODES,
    JOB_TITLE_CATEGORY_OTHER
} from "@mrdm-self-service-frontend/shared/data-access-definitions";
import { getEnumOptions } from "@mrdm-self-service-frontend/shared/helpers";
import { Gender, NameId } from "@mrdm-self-service-frontend/shared/types";
import { phoneValidator } from "@mrdm-self-service-frontend/shared/validators";
import { Invitation, AccountSetupParams } from "../../types";
import { distinctUntilChanged } from "rxjs/operators";
import { SharedLocalizationSettingsService } from "@mrdm-self-service-frontend/shared/util-logex-framework-setup";

@Component({
    selector: "mrdm-self-service-frontend-create-account-form",
    templateUrl: "./create-account.component.html",
    styleUrls: ["./create-account.component.scss"],
    providers: [useTranslationNamespace("APP._CreateAccount")],
    standalone: false
})
export class CreateAccountComponent extends BaseWithDefinitionsComponent implements OnChanges {
    private _fb = inject(UntypedFormBuilder);
    private _lgTranslate = inject(LgTranslateService);
    private _localizationSettings =
        inject<SharedLocalizationSettingsService>(LG_LOCALIZATION_SETTINGS);

    @Input() invitation?: Invitation;
    @Input() verifiedEmail?: string;
    @Input() showPasswordSection = true;
    @Input() saving!: boolean;
    @Output() readonly next: EventEmitter<AccountSetupParams> = new EventEmitter();

    _loading = false;
    _form: UntypedFormGroup;

    _genderOptions: NameId[] = [];
    _jobTitleCategoryDropdownDefinition: IDropdownDefinition<number> = {
        groups: []
    };

    _jobTitleRequired = false;

    constructor() {
        super();

        this._form = this._initForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.showPasswordSection) {
            this._updatePasswordValidation(changes.showPasswordSection.currentValue);
        }
    }

    _getRequiredDefinitions(): DefinitionKey[] {
        return ["jobTitleCategory"];
    }

    _activate(): void {
        this._loading = false;
        this._initDropdowns();
        if (this.invitation?.user) {
            this._form.patchValue(this.invitation.user);
        }
        this._form.patchValue({ email: this._email });
    }

    _save(): void {
        if (this._form.valid) {
            this.next.emit(this.accountData);
        }
    }

    public get accountData(): AccountSetupParams {
        return {
            ...this._form.value,
            email: this._email,
            locale: this._localizationSettings.prefferedLanguageWithoutInContext
        };
    }

    public get _email(): string {
        return this.invitation?.user.email || this.verifiedEmail || "";
    }

    private _initForm(): UntypedFormGroup {
        const group = this._fb.group({
            firstName: [null, [Validators.required]],
            lastName: [null, [Validators.required]],
            genderId: [Gender.Unknown, [Validators.required]],
            mobile: [null, [phoneValidator]],
            phone: [null, [phoneValidator]],
            jobTitleCategoryId: [null, [Validators.required]],
            jobTitle: [null],
            department: [null, [Validators.required]],
            password: [null, [Validators.required]],
            agreement: [false, [Validators.requiredTrue]]
        });

        group
            .get("jobTitleCategoryId")
            ?.valueChanges.pipe(distinctUntilChanged())
            .subscribe(value => {
                if (
                    value != null &&
                    this._definitions.jobTitleCategory[value]?.code === JOB_TITLE_CATEGORY_OTHER
                ) {
                    group.get("jobTitle")?.setValidators([Validators.required]);
                    this._jobTitleRequired = true;
                } else {
                    group.get("jobTitle")?.setValidators([]);
                    this._jobTitleRequired = false;
                }
                group.get("jobTitle")?.updateValueAndValidity();
            });

        return group;
    }

    private _updatePasswordValidation(showPasswordSection: boolean): void {
        if (showPasswordSection) {
            this._form.get("password")?.enable();
        } else {
            this._form.get("password")?.disable();
        }
    }

    private _initDropdowns(): void {
        this._genderOptions = getEnumOptions(Gender, "APP._Common.Enum.Gender.", this._lgTranslate);

        const jobTitleCategories = this._definitions.getSortedSectionData(
            "jobTitleCategory"
        ) as DefinitionJobTitleCategory[];

        const entries = jobTitleCategories
            .filter(category => !EXCLUDED_JOB_TITLE_CATEGORY_CODES.includes(category.code))
            .map(category => {
                const name = this._lgTranslate.translate(
                    `APP._Enums.JobTitleCategory.${category.code}`
                );
                return { ...category, name: name.startsWith("APP.") ? category.name : name };
            });
        this._jobTitleCategoryDropdownDefinition = {
            groups: [{ entries }],
            empty: this._lgTranslate.translate(".ContactInfo.Placeholders.JobTitleCategory")
        };
    }
}
